/**
 * Consultant Client Detail Page
 *
 * Detailed view of a specific client showing:
 * - Client company information
 * - Application status and progress
 * - Pre-audit checklist status
 * - Documents submitted
 * - Activity timeline
 * - Actions (start pre-audit, generate CAR, etc.)
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Mail, BarChart3, Check, RefreshCw, Eye, FileText } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { ProgressBar } from '../../components/common/ProgressBar';
import { formatDate } from '../../utils/formatting';
import { mockApi } from '../../services/mockApi';
import type { Application } from '../../types/application';

// Mock ID fallback
const MOCK_APPLICATION_ID = 'app-001';

export const ConsultantClientDetailPage: React.FC = () => {
  const { t } = useTranslation(['common', 'consultant', 'company']);
  const { id } = useParams<{ id: string }>();
  const applicationId = id || MOCK_APPLICATION_ID;

  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<Application | null>(null);

  useEffect(() => {
    loadApplicationDetails();
  }, [applicationId]);

  const loadApplicationDetails = async () => {
    setLoading(true);
    try {
      const response = await mockApi.getApplication(applicationId);
      if (response.success && response.data) {
        setApplication(response.data);
      }
    } catch (error) {
      console.error('Error loading application:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartPreAudit = () => {
    window.location.href = `/consultant/pre-audit/${applicationId}`;
  };

  const handleGenerateCAR = () => {
    window.location.href = `/consultant/car/${applicationId}`;
  };

  const handleGenerateReport = () => {
    window.location.href = `/consultant/reports/generate?client=${applicationId}`;
  };

  const handleContactClient = () => {
    alert(`Contacting client: ${application?.companyName}`);
  };

  const getStatusVariant = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'PRE_AUDIT_COMPLETED':
        return 'success';
      case 'CONSULTANT_ASSIGNED':
      case 'PRE_AUDIT_SCHEDULED':
        return 'warning';
      case 'REJECTED':
        return 'error';
      case 'UNDER_REVIEW':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <PageLayout
        pageTitle={t('consultant:clients.clientDetails')}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('consultant:dashboard.title'), href: '/consultant/dashboard' },
          { label: t('consultant:clients.title'), href: '/consultant/clients' },
          { label: t('consultant:clients.clientDetails') },
        ]}
      >
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">{t('common:app.loading')}</p>
        </div>
      </PageLayout>
    );
  }

  if (!application) {
    return (
      <PageLayout
        pageTitle={t('consultant:clients.clientDetails')}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('consultant:dashboard.title'), href: '/consultant/dashboard' },
          { label: t('consultant:clients.title'), href: '/consultant/clients' },
          { label: t('consultant:clients.clientDetails') },
        ]}
      >
        <div className="text-center py-12">
          <p className="text-gray-500">Application not found</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={application.companyName}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('consultant:dashboard.title'), href: '/consultant/dashboard' },
        { label: t('consultant:clients.title'), href: '/consultant/clients' },
        { label: application.companyName },
      ]}
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleContactClient} icon={<Mail className="w-4 h-4" />}>
            {t('consultant:actions.contactClient')}
          </Button>
          <Button variant="outline" onClick={handleGenerateReport} icon={<BarChart3 className="w-4 h-4" />}>
            {t('consultant:actions.viewReport')}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left/Center Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Application Overview */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Application Overview</h3>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Application Type</p>
                  <p className="mt-1">{application.type}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <div className="mt-1">
                    <Badge variant={getStatusVariant(application.status)}>
                      {application.status.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Submitted Date</p>
                  <p className="mt-1">{formatDate(application.submittedAt || application.createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Last Updated</p>
                  <p className="mt-1">{formatDate(application.updatedAt)}</p>
                </div>
              </div>

              {/* Completeness Progress */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-500">Application Completeness</p>
                  <p className="text-sm text-gray-700">{application.completenessScore}%</p>
                </div>
                <ProgressBar value={application.completenessScore} variant="default" />
              </div>
            </CardBody>
          </Card>

          {/* Company Information */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Company Information</h3>
            </CardHeader>
            <CardBody>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                  <dd className="mt-1 text-sm">{application.companyName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Applicant Name</dt>
                  <dd className="mt-1 text-sm">{application.applicantName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm">
                    <span className="text-gray-700">Contact via system</span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="mt-1 text-sm">Contact via system</dd>
                </div>
              </dl>
            </CardBody>
          </Card>

          {/* Status History */}
          {application.statusHistory && application.statusHistory.length > 0 && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Status History</h3>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  {application.statusHistory.map((history, index) => (
                    <div key={index} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0">
                      <div className="shrink-0 w-2 h-2 mt-2 bg-blue-500 rounded-full"></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <Badge variant={getStatusVariant(history.status)} size="sm">
                            {history.status.replace(/_/g, ' ')}
                          </Badge>
                          <span className="text-xs text-gray-500">{formatDate(history.changedAt)}</span>
                        </div>
                        {history.comment && (
                          <p className="text-sm text-gray-600 mt-1">{history.comment}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">{history.changedByName}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Sidebar - Right Column */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Quick Actions</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-2">
                {application.status === 'CONSULTANT_ASSIGNED' && (
                  <Button fullWidth onClick={handleStartPreAudit} icon={<Check className="w-4 h-4" />}>
                    {t('consultant:preAudit.startPreAudit')}
                  </Button>
                )}
                {application.status === 'PRE_AUDIT_SCHEDULED' && (
                  <Button fullWidth onClick={handleStartPreAudit} icon={<RefreshCw className="w-4 h-4" />}>
                    {t('consultant:preAudit.continuePreAudit')}
                  </Button>
                )}
                {application.status === 'PRE_AUDIT_COMPLETED' && (
                  <Button fullWidth onClick={handleStartPreAudit} icon={<Eye className="w-4 h-4" />}>
                    {t('consultant:preAudit.viewPreAudit')}
                  </Button>
                )}
                <Button variant="outline" fullWidth onClick={handleGenerateCAR} icon={<FileText className="w-4 h-4" />}>
                  {t('consultant:car.generate')}
                </Button>
                <Button variant="outline" fullWidth onClick={handleGenerateReport} icon={<BarChart3 className="w-4 h-4" />}>
                  {t('consultant:reports.generateReport')}
                </Button>
                <Button variant="outline" fullWidth onClick={handleContactClient} icon={<Mail className="w-4 h-4" />}>
                  {t('consultant:actions.contactClient')}
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Pre-Audit Status */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('consultant:preAudit.title')} Status</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Readiness Score</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <ProgressBar
                        value={application.completenessScore}
                        variant={
                          application.completenessScore >= 80 ? 'success' :
                          application.completenessScore >= 60 ? 'warning' : 'error'
                        }
                      />
                    </div>
                    <span className="text-sm font-medium">{application.completenessScore}%</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Pre-Audit Status</p>
                  <Badge variant={
                    application.status === 'PRE_AUDIT_COMPLETED' ? 'success' :
                    application.status === 'PRE_AUDIT_SCHEDULED' ? 'warning' : 'default'
                  }>
                    {application.status === 'PRE_AUDIT_COMPLETED' ? 'Completed' :
                     application.status === 'PRE_AUDIT_SCHEDULED' ? 'In Progress' : 'Not Started'}
                  </Badge>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Documents Summary */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Documents</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Documents</span>
                  <span className="font-medium">
                    {application.documents?.length || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Verified</span>
                  <span className="font-medium text-green-600">
                    {application.documents?.filter(d => d.verified).length || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Pending Review</span>
                  <span className="font-medium text-orange-600">
                    {application.documents?.filter(d => !d.verified).length || 0}
                  </span>
                </div>
                <Button variant="outline" fullWidth size="sm" className="mt-4"
                  onClick={() => window.location.href = `/consultant/clients/${applicationId}/documents`}>
                  View All Documents
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

